/**
 * Product editor UI state — the single reactive object that drives the
 * persistent Stage and the LeftRail / ContextPanel selection sync for
 * the canonical product editor.
 *
 * This is UI-only ephemeral state (what is selected, what the user is
 * pointed at). The persisted product / variant / decoration data lives
 * in the productEditor store (./productEditor.js); this store layers
 * selection + viewport on top.
 *
 * Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
 *       02-product-editor.md (Architecture - single persistent Stage)
 *
 * State fields:
 *   - selectedLeftRailItem: which Product Setup section / placement is active
 *   - selectedPlacementId: when a placement is selected; drives selectedView
 *   - selectedColorSku: from ColorsPanel swatch click
 *   - selectedView: DERIVED COMPUTED. Reads productEditor.decorations, finds
 *                   the placement matching selectedPlacementId, and resolves
 *                   its zone slug to the SKU's live Artifi view code via
 *                   resolveViewCodeForSku. Falls back to the SKU's first
 *                   FRONT-group view when no placement is selected, and to
 *                   null when the SKU's views are unavailable.
 *
 * Note: a legacy ``variantRoutingMap`` (colorSku -> A/B letter) plus
 * its setVariantRouting/clearVariantRouting actions lived here until
 * PR-N4 dropped the underlying variants.variant_key column. The
 * manual L/D override now lives durably on the campaign_product
 * (``manual_ld``) and is consulted by the kind-aware resolver
 * directly. See ``useVariantResolver`` for the replacement read path.
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    resolveViewCodeForSku,
    defaultViewForSku,
    frontViewForSku,
    backViewForSku,
    frontGroupCodesForSku,
    backGroupCodesForSku,
} from "@/utils/placementZoneToViewCode";
import { useProductEditorStore } from "@/stores/productEditor";

export const LEFT_RAIL_ITEMS = Object.freeze({
    SUPPLIER_SKU: "supplier-sku",
    SIZES_COLORS: "sizes-colors",
    PRICING: "pricing",
    STOREFRONT: "storefront",
    // Placements are dynamic — represented as `placement:<id>` strings.
});

// Which Product Setup tab the editor lands on when a product is opened
// (2026-07-09, Jake). ONE decision, expressed as a rule table so the
// landing policy lives in a single place instead of scattered watchers
// (design-philosophy: single-source state). Read the product's LOADED
// server attributes (dirty map is empty on a fresh open) and route the
// user to the first step that still needs attention:
//
//   - NEW product (no supplier SKU yet)          -> Supplier & SKU
//   - EXISTING, no sizes chosen                  -> Sizes & Colors
//   - EXISTING, has sizes but no colors chosen   -> Sizes & Colors
//   - EXISTING, has sizes AND colors             -> Storefront
//
// Sizes & Colors merge (2026-07-14): sizes and colors share one tab, so the
// middle two rules ("no sizes" and "no colors") both land on SIZES_COLORS.
// Pricing is deliberately NOT a landing target -- it is downstream of the
// size/color data-entry steps (a distributor lands to enter data, then tunes
// price).
//
// The rail deliberately does NOT land on a decoration placement anymore:
// the Stage preview still resolves the first placement's own view via
// `effectivePlacement`, so the preview renders correctly on the landing
// tab without a placement being SELECTED (which would drag the Stage onto
// setup tabs it is meant to stay hidden on).
export function computeInitialRailItem(attributes) {
    const attrs = attributes ?? {};
    const sku = attrs.product_item_number;
    // Empty string / null / undefined all mean "no SKU picked yet".
    if (sku == null || sku === "") return LEFT_RAIL_ITEMS.SUPPLIER_SKU;

    const sizes = attrs.selected_sizes;
    if (!Array.isArray(sizes) || sizes.length === 0) {
        return LEFT_RAIL_ITEMS.SIZES_COLORS;
    }

    const colorSkus = attrs.selected_color_skus;
    if (!Array.isArray(colorSkus) || colorSkus.length === 0) {
        return LEFT_RAIL_ITEMS.SIZES_COLORS;
    }

    return LEFT_RAIL_ITEMS.STOREFRONT;
}

export const useProductEditorUiState = defineStore("productEditorUiState", () => {
    const selectedLeftRailItem = ref(LEFT_RAIL_ITEMS.SUPPLIER_SKU);
    const selectedPlacementId = ref(null);
    const selectedColorSku = ref(null);

    // WHY the Stage's color last changed (Jake, 2026-10-07). One explicit
    // intent signal -- never a timing heuristic -- decides whether a color
    // change may call Artifi:
    //   'pick' -- the distributor clicked the color in "Colors on this
    //             product" (ColorFilmstrip, on every page that shows it): a
    //             deliberate choice, rendered at once.
    //   'tick' -- the color was ticked/unticked on Sizes & Colors
    //             (ColorsPanel). The Stage itself makes no Artifi call for it,
    //             but the render manager (useStorefrontColorRenders) renders a
    //             TICKED color at once (colorTickCount; Jake 2026-10-28) and
    //             nothing for an untick. Until it lands the Stage shows the
    //             supplier photo with a "Rendering..." indicator.
    //   'open' -- the Stage's own first pick when a product opens (nothing
    //             was selected before). Renders, as the Stage always has.
    //   'auto' -- the Stage re-picking because the selected color went away
    //             (a deselect). A side effect of ticking, so it does not
    //             render either.
    const selectedColorIntent = ref(null);
    // Bumped on every 'pick', so a re-pick of the SAME color still reads as
    // a fresh request.
    const colorPickCount = ref(0);
    // Bumped on every 'tick' (a tick OR an untick: ColorsPanel signals both
    // with the clicked sku), so ticking the same color again is a fresh
    // signal. The render manager decides whether the sku is still selected.
    const colorTickCount = ref(0);

    // Bug #1884: the CURRENT SKU's actual Artifi-configured views, fed in
    // by the Stage from GET /api/artifi/product_views (shape:
    // [{code, group, display_name?}]). When present, selectedView resolves
    // against these instead of the hardcoded SKU-18000 static map -- so a
    // polo/jacket that has no "Full Center" view never has that code sent
    // to Artifi. Empty/null falls back to the legacy static behavior so
    // 18000 / PC54 keep working before the views load (or on fetch error).
    const skuViews = ref(null);

    // Day 2: selectedPlacement / selectedView derive from selectedPlacementId
    // by looking up the productEditor store's decorations array. Both
    // expose helpful values to consumers (Stage, DecorationInspector)
    // and to one another (selectedView reads selectedPlacement to find
    // the zone code).
    //
    // The productEditor store is resolved lazily inside the computeds via
    // useProductEditorStore() rather than captured at store-definition time.
    // This keeps the import safe even when the productEditor store hasn't
    // been activated yet (e.g. when productEditorUiState is the first store
    // accessed in a test setup), since defineStore returns a factory that
    // takes the active Pinia instance from the caller.
    //
    // Gate round 1 P1 fix: previously Stage re-derived the same lookup
    // independently of selectedView. The selectedPlacement computed
    // collapses both into a single source of truth.
    const selectedPlacement = computed(() => {
        if (selectedPlacementId.value == null) return null;
        const editorStore = useProductEditorStore();
        const decorations = editorStore.decorations ?? [];
        return (
            decorations.find(
                (d) => (d.id ?? d._tempId) === selectedPlacementId.value,
            ) ?? null
        );
    });

    // Tier 1 activePreview (design-philosophy principle 4): the ONE
    // placement the Stage is looking at right now. The user's selection
    // when one exists; else the FIRST placement in display order; else
    // null (bare garment -- the product genuinely has no placements).
    //
    // Both the rendered VIEW (selectedView below) and the rendered
    // ARTWORK (Stage.vue activeVariant) derive from this single value,
    // so they can never describe two different placements -- the
    // back-logo-composited-onto-the-front-view bug class is
    // inexpressible by construction, with no watcher timing involved.
    // Because the preview derives from this fallback, the editor does NOT
    // auto-select the first placement on open -- doing so would highlight
    // a decoration in the rail and (via hasPlacementSelection) drag the
    // Stage onto the setup tabs it is meant to stay hidden on. The landing
    // tab is chosen by computeInitialRailItem instead.
    const effectivePlacement = computed(() => {
        if (selectedPlacement.value) return selectedPlacement.value;
        const editorStore = useProductEditorStore();
        const decorations = editorStore.decorations ?? [];
        return decorations[0] ?? null;
    });

    // Day 3 (audit gap #3): manual override for selectedView so the
    // Stage header's Front/Back toggle can flip the active view without
    // changing the selected placement. When set, takes precedence over
    // the placement-derived view code. Cleared whenever selectedPlacementId
    // changes so a fresh placement click re-derives from its zone code.
    const selectedViewOverride = ref(null);

    const selectedView = computed(() => {
        if (selectedViewOverride.value) return selectedViewOverride.value;
        // Tier 1 activePreview: derive from the EFFECTIVE placement (the
        // selection, else the first placement) so a fresh load previews
        // the first placement on ITS OWN zone -- never a back decoration
        // composited onto the front default.
        const placement = effectivePlacement.value;
        if (!placement) {
            // Bug #1884: with NO placements at all, default to the CURRENT
            // SKU's first FRONT-group view (null when no SKU views are
            // loaded -- Phase 3 #2224 removed the static "Full Center"
            // default; the Stage renders conservatively on a null view).
            return defaultViewForSku(skuViews.value);
        }
        // Resolve the zone SLUG (e.g. "left_chest"), never the placement
        // PRIMARY KEY. After the P6 PR-A Placement-substrate rename, a
        // view-model row's `placement_id` is the placement PK (a number,
        // e.g. 18) -- NOT a zone slug. Reading it here made the resolver
        // treat "18" as a zone and preview the wrong view (canvas-render
        // regression). resolveViewCodeForSku ignores a numeric-only slug
        // (no live view slugifies to a bare number).
        // The loader now threads the real zone slug from the placement_zone
        // include onto `placement_zone_slug`; read that. `placement` /
        // `zone` / `zone_code` remain supported for handoff fixtures and
        // legacy mocks. `placement_id` is deliberately NOT read as a zone.
        const zone =
            placement.placement_zone_slug ||
            placement.zone ||
            placement.zone_code ||
            placement.placement ||
            null;
        // Bug #1884: resolve against the SKU's real views so we never send
        // a view code the SKU lacks.
        return resolveViewCodeForSku({ zoneCode: zone, skuViews: skuViews.value });
    });

    function setSelectedView(viewCode) {
        // null/falsy clears the override and falls back to placement-derived.
        selectedViewOverride.value = viewCode || null;
    }

    function setLeftRailItem(item) {
        selectedLeftRailItem.value = item;
        // If a non-placement item is selected, clear the placement selection
        // so the Stage falls back to the default view.
        if (!item || !String(item).startsWith("placement:")) {
            selectedPlacementId.value = null;
        }
    }

    function setSelectedPlacement(placementId) {
        selectedPlacementId.value = placementId;
        // Day 3: clearing the manual view override when a placement is
        // picked re-derives the view from the placement's zone code. The
        // header toggle's effect should not survive a placement switch.
        selectedViewOverride.value = null;
        if (placementId != null) {
            selectedLeftRailItem.value = `placement:${placementId}`;
        }
    }

    /**
     * Move the Stage to a color, saying why (see ``selectedColorIntent``).
     *
     * @param {string|null} colorSku
     * @param {'pick'|'tick'|'open'|'auto'} [intent='pick']
     */
    function setSelectedColor(colorSku, intent = "pick") {
        selectedColorSku.value = colorSku;
        selectedColorIntent.value = colorSku == null ? null : intent;
        if (colorSku != null && intent === "pick") colorPickCount.value += 1;
        if (colorSku != null && intent === "tick") colorTickCount.value += 1;
    }

    // Bug #1884: fed by the Stage after it fetches the current SKU's
    // configured views from GET /api/artifi/product_views. null/empty
    // resets to legacy static behavior. Expects the endpoint shape:
    // [{code, group?, display_name?, thumbnail_url?}].
    function setSkuViews(views) {
        skuViews.value = Array.isArray(views) && views.length ? views : null;
    }

    // Tear-down for the editor shell (ProductEditor onUnmounted), alongside
    // productEditor.closeEditor(). This store is a SINGLETON that outlives
    // the component, and closeEditor resets the OTHER editor store only --
    // it never touched this one. So every selection here leaked from the
    // product the user just closed into the next one they opened.
    //
    // Which refs reset, and why each one:
    //
    //   selectedColorSku      -- the leak Jake reported (2026-09-01): the
    //                            editor must never preview a color that is
    //                            not enabled, and a color carried over from
    //                            another product is exactly that. Stage's
    //                            watcher re-validates once the new product's
    //                            colors load; clearing here means the wrong
    //                            color is never rendered even briefly.
    //   selectedPlacementId   -- a placement PK from product A does not
    //                            exist on product B, so the leaked id
    //                            resolves to null and the Stage silently
    //                            falls back. Harmless-looking, but it also
    //                            leaves selectedLeftRailItem pointing at
    //                            `placement:<id>` for a placement B lacks.
    //   selectedViewOverride  -- a manual Front/Back camera choice is a
    //                            gesture about the product being viewed, not
    //                            a durable preference; carrying it forward
    //                            can put a new product on a camera it has no
    //                            view for.
    //   skuViews              -- the PREVIOUS SKU's Artifi view codes. Stale
    //                            views are worse than none: resolveViewCode
    //                            treats them as authoritative, so a leaked
    //                            set can send a view code the new SKU lacks
    //                            (the exact class of bug #1884). Stage
    //                            re-feeds this per SKU, but not until its
    //                            fetch lands.
    //
    // selectedLeftRailItem is deliberately NOT reset: useProductEditorRouteSync
    // drives it from the URL on every open (applyRouteToStore runs on mount),
    // so it is re-derived from authority rather than carried over. Resetting
    // it here would race that sync for no benefit.
    function resetSelection() {
        selectedColorSku.value = null;
        selectedColorIntent.value = null;
        selectedPlacementId.value = null;
        selectedViewOverride.value = null;
        skuViews.value = null;
    }

    // SKU-aware Front/Back view codes + group sets for the StageHeader
    // pills, so the header never hardcodes "Full Center" for a product that
    // lacks it. Fall back to the static constants when skuViews are absent.
    const frontView = computed(() => frontViewForSku(skuViews.value));
    const backView = computed(() => backViewForSku(skuViews.value));
    const frontGroupCodes = computed(() =>
        frontGroupCodesForSku(skuViews.value),
    );
    const backGroupCodes = computed(() =>
        backGroupCodesForSku(skuViews.value),
    );
    // Whether this SKU has any back-of-garment view at all (Back pill hides
    // when false and live views are authoritative).
    const hasBackView = computed(() => backView.value != null);

    return {
        selectedLeftRailItem,
        selectedPlacementId,
        selectedColorSku,
        selectedColorIntent,
        colorPickCount,
        colorTickCount,
        selectedPlacement,
        effectivePlacement,
        selectedView,
        skuViews,
        frontView,
        backView,
        frontGroupCodes,
        backGroupCodes,
        hasBackView,
        setLeftRailItem,
        setSelectedPlacement,
        setSelectedColor,
        setSelectedView,
        setSkuViews,
        resetSelection,
    };
});
